import React, { useState, useEffect } from 'react';
import { Doughnut, Line } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Filler } from 'chart.js';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNotification } from '../context/NotificationContext';

// Import the CSS from the Dashboard component to reuse styles
import '../components/Dashboard/Dashboard.css'; 

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Filler);

// === HELPER API CONFIG (PRODUCTION READY) ===
const getApiUrl = () => {
    // Check for Vite env var
    if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_URL) {
        return import.meta.env.VITE_API_URL;
    }
    // Check for React Create App env var
    if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
    
    // Default to relative path for production (same domain)
    return '';
};

const API_URL = getApiUrl();

const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

// Helper to format numbers
const renderNumber = (value, decimals = 2, prefix = '', suffix = '') => {
    const num = parseFloat(value);
    if (isNaN(num)) return `N/A`;
    const formatted = num.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    return `${prefix}${formatted}${suffix}`;
};

// Component for the KPI Gauge Chart
function KpiGauge({ value, label }) {
    const data = {
        datasets: [{
            data: [value, 100 - value],
            backgroundColor: ['#588395', '#E0E0E0'],
            borderColor: ['#588395', '#E0E0E0'],
            circumference: 180,
            rotation: 270,
            cutout: '70%',
            borderWidth: 0,
            borderRadius: 5,
        }]
    };
    const options = { responsive: true, maintainAspectRatio: true, legend: { display: false }, tooltip: { enabled: false } };
    
    // --- FIX: Removed inline styles/wrappers to match Dashboard.js structure exactly ---
    return (
        <div className="gauge-container">
            <Doughnut data={data} options={options} />
            <div className="gauge-label">
                <div className="gauge-value">{renderNumber(value, 0, '', '%')}</div>
                <div className="gauge-text">{label}</div>
            </div>
        </div>
    );
}

// Component for displaying KPI change (e.g., +5% ▲, -3% ▼)
function KpiChange({ change }) {
    if (change === null || isNaN(change)) {
        return <div className="kpi-change neutral">N/A</div>;
    }
    const isPositive = change > 0;
    const arrow = isPositive ? '▲' : '▼';
    const sign = isPositive ? '+' : '';
    const className = isPositive ? 'kpi-change positive' : 'kpi-change negative';

    return (
        <div className={className}>
            {sign}{renderNumber(change, 1, '', '%')} {arrow}
        </div>
    );
}

// Component for a single Index Metric (e.g., MPI, ARI, RevPAR, RGI)
function IndexMetric({ value, label, change, prefix = '', decimals = 1 }) {
    return (
        <div className="index-metric-item">
            <div className="index-label">{label}</div>
            <div className="index-value">{renderNumber(value, decimals, prefix)}</div>
            <KpiChange change={change} />
        </div>
    );
}

// CompSet Pie Chart Component
function CompSetRoomsPieChart({ compsetRoomsBreakdown }) {
    const generateColors = (numColors) => {
        const colors = [];
        for (let i = 0; i < numColors; i++) {
            const hue = (i * 137) % 360; 
            colors.push(`hsl(${hue}, 50%, 65%)`); 
        }
        return colors;
    };

    const chartData = {
        labels: compsetRoomsBreakdown?.map(hotel => hotel.name) || [],
        datasets: [{
            data: compsetRoomsBreakdown?.map(hotel => hotel.percentage) || [],
            backgroundColor: generateColors(compsetRoomsBreakdown?.length || 0), 
            borderColor: '#fff',
            borderWidth: 2,
        }]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false, 
        plugins: {
            legend: {
                position: 'right', 
                labels: { boxWidth: 20 }
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        let label = context.label || '';
                        if (label) label += ': ';
                        if (context.parsed !== null) label += renderNumber(context.parsed, 1, '', '%');
                        return label;
                    }
                }
            }
        }
    };

    if (!compsetRoomsBreakdown || compsetRoomsBreakdown.length === 0) {
        return <div className="comp-set-pie-chart-placeholder">No data for CompSet Rooms Breakdown.</div>;
    }

    // --- FIX: Removed inline height to allow CSS to control sizing ---
    return (
        <div className="comp-set-pie-chart-container">
            <h4>Rooms Sold by CompSet Hotel</h4>
            <Doughnut data={chartData} options={chartOptions} />
        </div>
    );
}

// Component for each hotel card
function HotelCard({ initialHotelData }) {
    const hotel = initialHotelData;

    if (!hotel || !hotel.kpis || !hotel.hotel_id) {
        return <div className="hotel-card">Cargando datos del hotel...</div>;
    }

    const formatSubtitle = () => {
        if (!hotel.date_this_year || !hotel.date_last_year) {
            return hotel.date ? hotel.date.split('-').reverse().join('/') : 'Fecha no disponible';
        }
        try {
            const dateThisYear = parseISO(hotel.date_this_year);
            const dateLastYear = parseISO(hotel.date_last_year);
            const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
            const formattedThisYear = capitalize(format(dateThisYear, "EEEE, d 'de' MMMM, yyyy", { locale: es }));
            const formattedLastYear = capitalize(format(dateLastYear, "EEEE, d 'de' MMMM, yyyy", { locale: es }));
            return `${formattedThisYear} (comparado con ${formattedLastYear})`;
        } catch (e) {
            return hotel.date_this_year;
        }
    };

    const occupancyData = {
        labels: hotel.weekly_occupancy?.labels || [],
        datasets: [
            { label: 'Esta semana', data: hotel.weekly_occupancy?.this_week || [], borderColor: 'rgb(54, 162, 235)', backgroundColor: 'rgba(54, 162, 235, 0.2)', fill: true, tension: 0.4, },
            { label: 'El año pasado', data: hotel.weekly_occupancy?.last_year || [], borderColor: 'rgb(75, 192, 192)', backgroundColor: 'rgba(75, 192, 192, 0.2)', fill: true, tension: 0.4, }
        ]
    };

    const occupancyOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'top' },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        let label = context.dataset.label || '';
                        if (label) label += ': ';
                        if (context.parsed.y !== null) label += Math.round(context.parsed.y) + '%';
                        return label;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                max: 115,
                ticks: { callback: (value) => `${value}%` }
            },
            x: {
                display: true,
                ticks: {
                    autoSkip: false,
                    maxRotation: 45,
                    minRotation: 45,
                    color: '#343a40',
                    font: { size: 11, weight: 'bold' }
                },
                grid: { display: false }
            }
        }
    };

    const finalImageUrl = hotel.image_url ? hotel.image_url : `https://placehold.co/200x150/A0A0A0/FFFFFF?text=${encodeURIComponent(hotel.name)}`;

    return (
        <div className="hotel-card">
            <div className="hotel-card-header">
                <img src={finalImageUrl} alt={hotel.name} className="hotel-image" onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/200x150/CCCCCC/666666?text=Image+Missing" }} />
                <div className="hotel-title">
                    <h3>{hotel.name}</h3>
                    <p>{formatSubtitle()}</p>
                </div>
            </div>
            <div className="hotel-card-body hotel-card-grid">
                <div className="metrics-and-chart-column">
                    <div className="compset-display-card">
                        <span className="compset-label">CompSet (Default):</span>
                        <span className="compset-name">
                            {hotel.effective_compset_name || `ID: ${hotel.effective_compset_id}`}
                        </span>
                    </div>

                    <div className="kpi-section">
                        <div className="kpi-group">
                            <KpiGauge value={hotel.kpis?.occupancy?.value} label="Ocupación" />
                            <KpiChange change={hotel.kpis?.occupancy?.change} />
                        </div>
                        <div className="kpi-group">
                            <div className="kpi-big-number">
                                <div className="kpi-value">{renderNumber(hotel.kpis?.adr?.value, 2, '$')}</div>
                                <div className="kpi-label">ADR</div>
                            </div>
                            <KpiChange change={hotel.kpis?.adr?.change} />
                        </div>
                    </div>
                    {/* --- FIX: Removed inline height on chart section --- */}
                    <div className="chart-section">
                        <h4>Ocupación Semanal</h4>
                        <Line options={occupancyOptions} data={occupancyData} />
                    </div>
                </div>

                <div className="indices-and-pie-column">
                    <div className="indices-column">
                        <h4>Índices (Comp Set)</h4>
                        <div className="indices-grid">
                            <IndexMetric label="MPI" value={hotel.indices?.mpi?.value} change={hotel.indices?.mpi?.change} />
                            <IndexMetric label="ARI" value={hotel.indices?.ari?.value} change={hotel.indices?.ari?.change} />
                            <IndexMetric label="RevPAR" value={hotel.indices?.revpar?.value} change={hotel.indices?.revpar?.change} prefix="$" decimals={2} />
                            <IndexMetric label="RGI" value={hotel.indices?.rgi?.value} change={hotel.indices?.rgi?.change} />
                        </div>
                    </div>
                    <div className="comp-set-chart-column">
                        <CompSetRoomsPieChart compsetRoomsBreakdown={hotel.compset_rooms_breakdown_for_card} />
                    </div>
                </div>
            </div>
        </div>
    );
}

// === MAIN COMPONENT ===
function DashboardIndividual() {
    const { showNotification } = useNotification();
    const [dashboardData, setDashboardData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchDashboardData = async () => {
            setLoading(true);
            setError(null);
            try {
                // Using the specific individual dashboard endpoint with the configured API_URL
                const response = await fetch(`${API_URL}/api.php?path=dashboard-individual`, {
                    headers: getAuthHeader()
                });
                
                // Robust error handling
                if (!response.ok) {
                    const errorText = await response.text();
                    let errorMessage = `HTTP error! Status: ${response.status}`;
                    try {
                        const errorBody = JSON.parse(errorText);
                        errorMessage = errorBody.error?.message || errorMessage;
                    } catch (e) {
                         // Fallback for non-JSON errors
                    }
                    throw new Error(errorMessage);
                }

                const json = await response.json();

                if (json.success) {
                    // Expecting dashboardData to be an array of hotels, just like the main dashboard
                    if (json.data && json.data.dashboardData) {
                         setDashboardData(json.data.dashboardData);
                    } else if (Array.isArray(json.data)) {
                         setDashboardData(json.data);
                    } else {
                         // Fallback if data is empty or different structure
                         setDashboardData([]);
                    }
                } else {
                    showNotification(json.error?.message || 'Error cargando dashboard', 'error');
                }
            } catch (error) {
                console.error('Error fetching individual dashboard:', error);
                setError(error.message);
                showNotification('Error de conexión', 'error');
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [showNotification]);

    if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>Cargando Dashboard...</div>;
    if (error) return <div style={{ padding: '40px', textAlign: 'center', color: 'red' }}>Error: {error}</div>;
    if (!dashboardData || dashboardData.length === 0) return <div style={{ padding: '40px', textAlign: 'center' }}>No hay datos disponibles para mostrar.</div>;

    return (
        <div className="dashboard-container">
            <h1 className="page-title">Dashboard Individual</h1>
            <div className="dashboard-grid">
                {dashboardData.map(hotel => (
                    <HotelCard
                        key={hotel.hotel_id}
                        initialHotelData={hotel}
                    />
                ))}
            </div>
        </div>
    );
}

export default DashboardIndividual;